/**
 * Session state: the branching graph, the path taken through it, and the actions that
 * move it.
 *
 * The reducer is pure. Every side effect — starting the recogniser, calling `/api`,
 * writing to IndexedDB, speaking — lives in the store *around* the reducer. That is
 * what makes the whole session replayable from an action log, which is how recogniser
 * regressions get diagnosed without a piano.
 *
 * ## Ownership
 *
 * This file (the types, and `createInitialSessionState`) is frozen contract.
 * `reducer.ts`, `matchBranch.ts` and `keyEstimate.ts` are implemented separately; the
 * signatures they must satisfy are declared at the bottom of this file as
 * `SessionReducer`, `MatchBranch` and `EstimateKey`.
 */

import type { CanonicalChord } from '../chord/types.js';
import type { PitchClass } from '../chord/types.js';
import type { DetectedChord } from '../recognition/types.js';
import type { BranchSuggestion } from '../llm/schemas.js';

/**
 * The stylistic lenses the player can have active. Several at once is normal and
 * interesting — "jazz + cinematic" is a different graph from either alone.
 */
export const PERSPECTIVES = [
  'pop',
  'jazz',
  'classical',
  'romantic',
  'modal',
  'cinematic',
  'experimental',
] as const;

export type Perspective = (typeof PERSPECTIVES)[number];

/**
 * The running estimate of where we are tonally.
 *
 * `confidence` is 0..1 and is genuinely allowed to be low — three chords of planing
 * fourths have no tonic, and pretending otherwise produces confident nonsense in the
 * roman numerals. `secondBest` exists so the UI can say "or possibly…" instead.
 */
export interface KeyEstimate {
  tonicPc: PitchClass;
  mode: 'major' | 'minor';
  confidence: number;
  secondBest?: { tonicPc: PitchClass; mode: 'major' | 'minor' };
}

/** One chord actually played, in order. The path through the graph. */
export interface SessionNode {
  id: string;
  chord: CanonicalChord;
  /** The raw detection, kept so a later correction can be re-scored against the chroma. */
  detected?: DetectedChord;
  source: 'audio' | 'correction' | 'manual';
  /** Set when this chord matched an offered branch. Absent when the player went their own way. */
  fromSuggestionId?: string;
  playedAt: number;
  /** The key estimate as it stood when this chord was played — not the current one. */
  keyAtTime: KeyEstimate;
}

/**
 * The branches offered from one node.
 *
 * `status` covers the streaming case: suggestions arrive one at a time, so the graph
 * can start drawing at `partial` rather than waiting for the whole set. `fallback`
 * means the LLM failed and these are locally generated, theory-derived continuations —
 * the app keeps working without a network.
 */
export interface BranchSet {
  forNodeId: string;
  suggestions: BranchSuggestion[];
  status: 'loading' | 'partial' | 'ready' | 'failed' | 'fallback';
  requestedAt: number;
}

export interface SessionState {
  sessionId: string;
  startedAt: number;
  /** Incremented on every state change. Used for persistence debouncing and optimistic UI. */
  revision: number;
  /** Free text the player set, e.g. "something that feels like leaving". */
  intention?: string;
  perspectives: Perspective[];
  /** 0..1. */
  adventurousness: number;
  path: SessionNode[];
  currentNodeId: string | null;
  /** Keyed by `SessionNode.id`. Pruned as the path moves on. */
  branchSets: Record<string, BranchSet>;
  keyEstimate: KeyEstimate;
  /** False after a failed branch request, so the UI can be honest about being offline. */
  llmHealthy: boolean;
  listening: 'idle' | 'awaiting-first-chord' | 'active' | 'paused';
  micStatus: 'unrequested' | 'granted' | 'denied' | 'unavailable';
}

export type SessionAction =
  | { type: 'SESSION_STARTED'; sessionId: string; intention?: string; perspectives: Perspective[] }
  | { type: 'MIC_STATUS'; status: SessionState['micStatus'] }
  /**
   * A new harmony was recognised. This is the action the whole design turns on, so the
   * required behaviour is spelled out rather than left to taste.
   *
   * 1. **Same chord, still sounding.** If the detected pitch-class set equals the current
   *    node's set — inversion-insensitive, so compare the sets, not the bass — do not
   *    append a node. The player is holding or re-voicing the same harmony; extend the
   *    existing node's duration instead.
   *
   *    **Arpeggiation.** If the detected set is a strict superset of the current node's
   *    set and the detection arrived within `arpeggioWindowMs` of that node, replace the
   *    current node's chord *in place* rather than appending. Rolling C–E–G–B♭ is one
   *    Cmaj7 becoming clearer, not four chords. See `pcSetIsStrictSuperset`.
   *
   * 2. **Otherwise, try the offered branches.** Run `matchBranch` against the current
   *    node's branch set. A match is pitch-class-set equality **ignoring the bass**, so a
   *    C minor played in first inversion matches a plain "C minor" branch. When it
   *    matches: append a node carrying the *actually detected* bass and inversion — not
   *    the suggestion's — and set `fromSuggestionId` to the matched suggestion.
   *
   * 3. **No match: append it anyway.** `source: action.source ?? 'audio'`, no
   *    `fromSuggestionId`. The player ignoring every suggestion and playing something else
   *    is the product working as intended. There is **no error state here, ever** — no
   *    "unrecognised chord" banner, no rejection, no snapping to the nearest suggestion.
   *
   * 4. **Either way, afterwards:** recompute `keyEstimate` from the new path via
   *    `estimateKey`, `respell` the new chord against that estimate, set `currentNodeId`
   *    to the new node, and drop branch sets belonging to nodes older than the previous
   *    node (the graph keeps one step of history visible, not the whole session).
   *
   * 5. **Side effects stay outside.** Requesting the next branch set, persisting, and
   *    speaking all happen in the store around the reducer. The reducer never calls
   *    `fetch`, never reads a clock it was not given, and never generates an id it was
   *    not given — ids and timestamps come in on the action or from an injected factory,
   *    so replay is deterministic.
   *
   * 6. **How the chord was selected is not how it is handled.** `source` says whether the
   *    harmony arrived from a continuous stream (the microphone, later MIDI) or from a
   *    deliberate discrete choice — a branch card tapped on a train, a chord picked or
   *    typed. It changes exactly one thing: the `source` recorded on the appended node,
   *    which is what stops "possibly…" chrome appearing over a chord somebody chose by
   *    name. Matching, key estimation, pruning and the arpeggio window are identical, and
   *    that is the point: there is one harmony engine, and selecting a chord is selecting
   *    a chord however the selection was made. Absent means `'audio'`, so every existing
   *    caller and every recorded action log means what it always did.
   *
   * 7. **A selection also carries its own spelling — see `chosen`.** That is the one thing
   *    a choice knows and a hearing cannot, and it is a spelling only: step 4 still stands
   *    for everything else.
   */
  | {
      type: 'CHORD_DETECTED';
      detected: DetectedChord;
      source?: 'audio' | 'manual';
      /**
       * The fully spelled chord the player explicitly picked, present only when this
       * harmony was *chosen* rather than heard.
       *
       * The recogniser can never produce this. A microphone hears a sonority and a sonority
       * has no letters (rule 1: the recogniser emits pitch classes, never spellings), so an
       * audio dispatch hands over pitch classes and the session names them with `respell`
       * against the key. A selection is the other way round: the player has already read
       * "D♭m" on the card they tapped, and the app renaming it C♯m in the same breath —
       * because that very chord dragged the key estimate onto the sharp side — is the app
       * contradicting what it had just said. This field is how a selection's spelling
       * intent travels the one step from the tap to the node; audio dispatches never carry
       * it.
       *
       * Honoured only where it describes the detection it arrived with: the same
       * pitch-class set and the same bass. Otherwise — and always for audio — the node is
       * spelled mechanically, byte for byte as it always was. Nothing else reads it:
       * matching, key estimation, the arpeggio window and pruning all run off pitch
       * classes, so this decides letters and only letters.
       */
      chosen?: CanonicalChord;
    }
  | { type: 'BRANCHES_LOADING'; nodeId: string }
  /** One streamed suggestion. Moves `status` to `partial`. */
  | { type: 'BRANCH_RECEIVED'; nodeId: string; suggestion: BranchSuggestion }
  | { type: 'BRANCHES_READY'; nodeId: string }
  /** `fallback` carries locally generated continuations, if any were produced. */
  | { type: 'BRANCHES_FAILED'; nodeId: string; fallback?: BranchSuggestion[] }
  /** The player fixed a misheard chord. The node keeps its position; `source` becomes 'correction'. */
  | { type: 'CORRECTION_APPLIED'; nodeId: string; chord: CanonicalChord }
  | { type: 'STEP_BACK'; count: number }
  | { type: 'SET_INTENTION'; intention: string }
  | { type: 'SET_PERSPECTIVES'; perspectives: Perspective[] }
  | { type: 'SET_ADVENTUROUSNESS'; value: number }
  | { type: 'LLM_HEALTH'; healthy: boolean }
  /** Rehydration from IndexedDB. Replaces state wholesale; validate before dispatching. */
  | { type: 'RESTORED'; state: SessionState };

/** C major, no confidence — what you have before a single note has been played. */
export const DEFAULT_KEY_ESTIMATE: Readonly<KeyEstimate> = Object.freeze({
  tonicPc: 0,
  mode: 'major',
  confidence: 0,
});

/** A sensible starting lens for someone who has not chosen one. */
export const DEFAULT_PERSPECTIVES: readonly Perspective[] = Object.freeze(['pop', 'jazz'] as const);

/** Middle of the road: half the branches obvious, half not. */
export const DEFAULT_ADVENTUROUSNESS = 0.5;

/** How many branches to ask for. The brief's range is 4..6. */
export const MIN_BRANCH_COUNT = 4;
export const MAX_BRANCH_COUNT = 6;
export const DEFAULT_BRANCH_COUNT = 5;

export interface CreateInitialSessionStateOptions {
  sessionId: string;
  /** Defaults to `Date.now()`. Pass it explicitly in tests so replays are deterministic. */
  startedAt?: number;
  intention?: string;
  perspectives?: Perspective[];
  adventurousness?: number;
  keyEstimate?: KeyEstimate;
}

/**
 * The empty session. Everyone needs this — the store to initialise, the reducer to
 * handle `SESSION_STARTED`, the tests to build fixtures — so it lives here rather than
 * in the reducer file.
 */
export function createInitialSessionState(options: CreateInitialSessionStateOptions): SessionState {
  const state: SessionState = {
    sessionId: options.sessionId,
    startedAt: options.startedAt ?? Date.now(),
    revision: 0,
    perspectives: [...(options.perspectives ?? DEFAULT_PERSPECTIVES)],
    adventurousness: options.adventurousness ?? DEFAULT_ADVENTUROUSNESS,
    path: [],
    currentNodeId: null,
    branchSets: {},
    keyEstimate: options.keyEstimate ?? { ...DEFAULT_KEY_ESTIMATE },
    llmHealthy: true,
    listening: 'idle',
    micStatus: 'unrequested',
  };
  if (options.intention !== undefined) state.intention = options.intention;
  return state;
}

/** The node the path is currently sitting on, or `null` before the first chord. */
export function currentNode(state: SessionState): SessionNode | null {
  if (!state.currentNodeId) return null;
  return state.path.find((node) => node.id === state.currentNodeId) ?? null;
}

/** The branch set offered from the current node, if one has been requested. */
export function currentBranchSet(state: SessionState): BranchSet | null {
  if (!state.currentNodeId) return null;
  return state.branchSets[state.currentNodeId] ?? null;
}

// ---------------------------------------------------------------------------
// Declared signatures — implemented elsewhere.
//
// `reducer.ts`, `matchBranch.ts` and `keyEstimate.ts` are owned by the session
// implementation chunk. Their exports MUST satisfy these types; annotate them, e.g.
//
//     export const sessionReducer: SessionReducer = (state, action) => { ... };
//
// so that a drift between the contract and the implementation is a compile error
// rather than a runtime surprise.
// ---------------------------------------------------------------------------

/**
 * `sessionReducer(state, action) => state`, implemented in `session/reducer.ts`.
 * Pure: no clocks, no ids, no I/O. See the doc comment on `CHORD_DETECTED` for the
 * behaviour that action must produce.
 */
export type SessionReducer = (state: SessionState, action: SessionAction) => SessionState;

/**
 * `matchBranch(detected, set) => suggestion | null`, implemented in `session/matchBranch.ts`.
 *
 * Matching is pitch-class-set equality **ignoring the bass**: the suggestion's chord is
 * converted with `toCanonical` and its `pitchClasses` compared to `detected.best.pitchClasses`
 * with `pcSetEquals`. Inversions match. Returns the first (highest-ranked) match, or `null`.
 */
export type MatchBranch = (detected: DetectedChord, set: BranchSet) => BranchSuggestion | null;

/**
 * `estimateKey(path) => KeyEstimate`, implemented in `session/keyEstimate.ts`.
 *
 * Weighted toward the recent end of the path, and honest about low confidence. Called
 * after every appended node; the result becomes `keyAtTime` on that node and drives
 * `respell`.
 */
export type EstimateKey = (path: SessionNode[]) => KeyEstimate;
